const QUOTE_INPUT_NAMES = Object.freeze([
  'selection_label',
  'delivery_warehouse_address',
  'quote_dollars',
  'quote_terms',
]);
const QUOTE_DOLLARS_PATTERN = /^\d{1,7}(\.\d{1,2})?$/u;

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function textValue(value) {
  return String(value ?? '').trim();
}

function translator(t) {
  return (key, fallback) => {
    if (typeof t !== 'function') return fallback;
    const value = t(key);
    return typeof value === 'string' && value.trim() && value !== key ? value : fallback;
  };
}

function formatQuoteCents(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((Number(cents) || 0) / 100);
}

export function quoteLocationRows(locations = []) {
  const rows = (Array.isArray(locations) ? locations : []).flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const locationId = textValue(value.location_id ?? value.id);
    const selectionLabel = textValue(value.selection_label);
    if (!locationId || !selectionLabel) return [];
    const centsValue = value.quote_cents ?? value.base_quote_cents;
    const quoteCents = Number.isSafeInteger(centsValue) && centsValue >= 0
      ? Number(centsValue) : null;
    const revisionValue = Number(value.revision);
    return [Object.freeze({
      location_id: locationId,
      selection_label: selectionLabel,
      delivery_warehouse_address: textValue(value.delivery_warehouse_address ?? value.address),
      quote_display: textValue(value.quote_display)
        || (quoteCents === null ? '' : formatQuoteCents(quoteCents)),
      quote_cents: quoteCents,
      quote_terms: textValue(value.quote_terms),
      active: value.active !== false,
      revision: Number.isSafeInteger(revisionValue) && revisionValue >= 0 ? revisionValue : 0,
    })];
  });
  rows.sort((left, right) => (
    Number(right.active) - Number(left.active)
    || left.selection_label.localeCompare(right.selection_label, 'en', { sensitivity: 'base' })
    || left.location_id.localeCompare(right.location_id)
  ));
  return Object.freeze(rows);
}

export function validateQuoteLocationInput(values, { t } = {}) {
  const label = translator(t);
  const source = values && typeof values === 'object' && !Array.isArray(values) ? values : {};
  const errors = {};
  const selectionLabel = textValue(source.selection_label);
  if (!selectionLabel) {
    errors.selection_label = label('quoteAdmin.labelRequired', 'Selection label is required.');
  } else if (selectionLabel.length > 600) {
    errors.selection_label = label('quoteAdmin.labelTooLong', 'Selection label must be 600 characters or fewer.');
  }
  const address = textValue(source.delivery_warehouse_address);
  if (!address) {
    errors.delivery_warehouse_address = label('quoteAdmin.addressRequired', 'Delivery warehouse address is required.');
  } else if (address.length > 500) {
    errors.delivery_warehouse_address = label('quoteAdmin.addressTooLong', 'Delivery warehouse address must be 500 characters or fewer.');
  }
  let quoteCents = null;
  const quoteRaw = textValue(source.quote_dollars).replace(/^\$/u, '').replaceAll(',', '');
  if (!quoteRaw) {
    errors.quote_dollars = label('quoteAdmin.quoteRequired', 'Quote amount is required.');
  } else if (!QUOTE_DOLLARS_PATTERN.test(quoteRaw)) {
    errors.quote_dollars = label('quoteAdmin.quoteInvalid', 'Enter a quote in dollars with up to two decimals.');
  } else {
    quoteCents = Math.round(Number(quoteRaw) * 100);
  }
  if (textValue(source.quote_terms).length > 5000) {
    errors.quote_terms = label('quoteAdmin.termsTooLong', 'Quote terms must be 5000 characters or fewer.');
  }
  return Object.freeze({
    ok: Object.keys(errors).length === 0,
    errors: Object.freeze(errors),
    quote_cents: quoteCents,
  });
}

function quoteLocationRowMarkup(row, label, { allCustomers = false, canManage = true } = {}) {
  const state = row.active
    ? `<span class="quote-admin__state quote-admin__state--active">${escapeHtml(label('quoteAdmin.active', '启用'))}</span>`
    : `<span class="quote-admin__state quote-admin__state--inactive">${escapeHtml(label('quoteAdmin.inactive', '已停用'))}</span>`;
  // Deactivate used to be one-way: a mistyped address could be switched off and
  // never switched back from this page. The backend has always accepted an
  // upsert carrying active: true, so the row now toggles both ways, and Edit
  // loads the row into the form rather than forcing a retype.
  const action = `<button type="button" class="quote-admin__edit" data-quote-admin-edit data-location-id="${escapeHtml(row.location_id)}">${escapeHtml(label('quoteAdmin.edit', '编辑'))}</button>`
    + (row.active
      ? `<button type="button" class="quote-admin__deactivate" data-quote-admin-deactivate data-location-id="${escapeHtml(row.location_id)}">${escapeHtml(label('quoteAdmin.deactivate', '停用'))}</button>`
      : `<button type="button" class="quote-admin__activate" data-quote-admin-activate data-location-id="${escapeHtml(row.location_id)}">${escapeHtml(label('quoteAdmin.activate', '启用'))}</button>`);
  // Across customers the row is no longer addressable by location_id alone -
  // two customers can hold the same address - so All is a reading view: it names
  // the customer and drops the per-row action rather than offering a Deactivate
  // that would have to guess whose address it is.
  const customerCell = allCustomers
    ? `<td class="quote-admin__cell quote-admin__cell--customer">${escapeHtml(row.customer_label || '—')}</td>`
    : '';
  // Only the roles the callable accepts get the buttons. Dispatch and Clerk read
  // this list so their Delivery Address menus match Admin's, but every one of
  // these three buttons is a write - assertQuoteLocationManagerActor allows Admin
  // and Finance alone - so drawing them for a reader is drawing a button that
  // answers "Only Admin or Finance can manage Customer quote locations". A
  // control that cannot work should not be on screen.
  const actionCell = allCustomers || !canManage
    ? ''
    : `<td class="quote-admin__cell quote-admin__cell--actions">${action}</td>`;
  return `<tr class="quote-admin__row${row.active ? '' : ' quote-admin__row--inactive'}" data-quote-admin-row="${escapeHtml(row.location_id)}">
      ${customerCell}<td class="quote-admin__cell quote-admin__cell--label">${escapeHtml(row.selection_label)}</td>
      <td class="quote-admin__cell quote-admin__cell--address">${escapeHtml(row.delivery_warehouse_address || '—')}</td>
      <td class="quote-admin__cell quote-admin__cell--quote">${escapeHtml(row.quote_display || '—')}</td>
      <td class="quote-admin__cell quote-admin__cell--terms" title="${escapeHtml(row.quote_terms || '')}">${escapeHtml(row.quote_terms || '—')}</td>
      <td class="quote-admin__cell quote-admin__cell--state">${state}</td>
      ${actionCell}
    </tr>`;
}

// Adding a delivery address sets a price, so among staff only Finance may do it
// (Alan, 2026-08-02). Admin keeps it through the standing override it has
// everywhere else. Everyone else sees the list without the form - not a disabled
// form, no form at all.
const QUOTE_ADMIN_EDIT_ROLES = Object.freeze(['admin', 'finance']);

export function canAddQuoteLocation(role) {
  return QUOTE_ADMIN_EDIT_ROLES.includes(String(role ?? '').trim().toLowerCase());
}

export function quoteLocationAdminMarkup({
  customerLabel = '',
  rows = [],
  role = '',
  allCustomers = false,
} = {}, { t } = {}) {
  const label = translator(t);
  const canManage = canAddQuoteLocation(role);
  const safeRows = Array.isArray(rows) ? rows : [];
  const columns = allCustomers ? 6 : 6;
  const emptyCopy = allCustomers
    ? label('quoteAdmin.emptyAll', 'No delivery addresses recorded for any customer.')
    : label('quoteAdmin.empty', 'No delivery addresses recorded for this customer.');
  const body = safeRows.length
    ? safeRows.map((row) => quoteLocationRowMarkup(row, label, { allCustomers, canManage })).join('')
    : `<tr><td colspan="${columns}" class="quote-admin__empty">${escapeHtml(emptyCopy)}</td></tr>`;
  const headCells = [
    allCustomers ? label('quoteAdmin.columnCustomer', 'Customer') : null,
    label('quoteAdmin.columnLabel', 'Label'),
    label('quoteAdmin.columnAddress', 'Address'),
    label('quoteAdmin.columnQuote', 'Quote'),
    label('quoteAdmin.columnTerms', 'Terms'),
    label('quoteAdmin.columnActive', 'Active'),
    allCustomers || !canManage ? null : label('quoteAdmin.columnActions', 'Actions'),
  ].filter((value) => value !== null);
  return `<section class="quote-admin" data-quote-admin${allCustomers ? ' data-quote-admin-all' : ''}>
    <header class="quote-admin__heading">
      <h2>${escapeHtml(label('quoteAdmin.title', 'Delivery Addresses & Quotes'))}</h2>
      <span class="quote-admin__customer">${escapeHtml(textValue(customerLabel) || '—')}</span>
    </header>
    <p class="quote-admin__error" data-quote-admin-error role="alert" aria-live="assertive"></p>
    <div class="quote-admin__table-wrap">
      <table class="quote-admin__table">
        <thead><tr>${headCells.map((cell) => `<th>${escapeHtml(cell)}</th>`).join('')}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
    ${allCustomers && canManage ? `
    <p class="quote-admin__pick-customer" data-quote-admin-pick-customer>${escapeHtml(label(
      'quoteAdmin.pickCustomerToManage',
      'Pick one customer above to add or change a quote. 选择上面某一个客户，才能添加或修改报价。',
    ))}</p>` : ''}
    ${!allCustomers && canManage ? `
    <form class="quote-admin__form" data-quote-admin-form novalidate>
      <h3 class="quote-admin__form-title">${escapeHtml(label('quoteAdmin.addTitle', 'Add Delivery Address & Quote'))}</h3>
      <div class="quote-admin__form-grid">
        <label class="quote-admin__field"><span>${escapeHtml(label('quoteAdmin.selectionLabel', 'Selection Label'))}</span><input name="selection_label" maxlength="600" required><small data-quote-admin-field-error="selection_label"></small></label>
        <label class="quote-admin__field"><span>${escapeHtml(label('quoteAdmin.deliveryAddress', 'Delivery Warehouse Address'))}</span><input name="delivery_warehouse_address" maxlength="500" required><small data-quote-admin-field-error="delivery_warehouse_address"></small></label>
        <label class="quote-admin__field"><span>${escapeHtml(label('quoteAdmin.quoteDollars', 'Quote (USD)'))}</span><input name="quote_dollars" inputmode="decimal" required><small data-quote-admin-field-error="quote_dollars"></small></label>
        <label class="quote-admin__field quote-admin__field--wide"><span>${escapeHtml(label('quoteAdmin.quoteTerms', 'Quote Terms'))}</span><textarea name="quote_terms" maxlength="5000"></textarea><small data-quote-admin-field-error="quote_terms"></small></label>
      </div>
      <p class="quote-admin__form-error" data-quote-admin-form-error role="alert" aria-live="assertive"></p>
      <button type="submit" class="quote-admin__submit" data-quote-admin-submit>${escapeHtml(label('quoteAdmin.save', 'Save Address & Quote'))}</button>
    </form>` : ''}
  </section>`;
}

export function mountQuoteLocationAdmin({
  host,
  customerUid = '',
  customerLabel = '',
  role = '',
  deps = {},
} = {}) {
  const { listLocations, upsertLocation, deactivateLocation, t } = deps;
  if (!host || typeof listLocations !== 'function' || typeof upsertLocation !== 'function'
    || typeof deactivateLocation !== 'function') {
    return Object.freeze({ ready: Promise.resolve(), destroy() {} });
  }
  const label = translator(t);
  const controller = new AbortController();
  const { signal } = controller;
  const customer_uid = textValue(customerUid);
  let resolvedCustomerLabel = textValue(customerLabel);
  let destroyed = false;
  let currentRows = [];
  // Set while an existing row is being edited. An upsert carrying a location_id
  // updates that row; without one it creates a new address, so losing track of
  // this turns a correction into a duplicate.
  let editingLocationId = '';

  const readValues = (form) => Object.fromEntries(QUOTE_INPUT_NAMES.map((name) => [
    name,
    form.querySelector?.(`[name="${name}"]`)?.value ?? '',
  ]));

  const showFieldErrors = (form, errors) => {
    QUOTE_INPUT_NAMES.forEach((name) => {
      const slot = form.querySelector?.(`[data-quote-admin-field-error="${name}"]`);
      if (slot) slot.textContent = errors[name] || '';
    });
  };

  const wire = () => {
    const form = host.querySelector?.('[data-quote-admin-form]');
    form?.addEventListener('submit', async (event) => {
      event.preventDefault?.();
      const formError = form.querySelector?.('[data-quote-admin-form-error]');
      const submit = form.querySelector?.('[data-quote-admin-submit]');
      const values = readValues(form);
      const validated = validateQuoteLocationInput(values, { t });
      showFieldErrors(form, validated.errors);
      if (!validated.ok) {
        if (formError) formError.textContent = label('quoteAdmin.fixFields', 'Fix the highlighted fields, then save again.');
        return;
      }
      if (formError) formError.textContent = '';
      if (submit) submit.disabled = true;
      try {
        await upsertLocation({
          customer_uid,
          ...(editingLocationId ? { location_id: editingLocationId } : {}),
          selection_label: textValue(values.selection_label),
          delivery_warehouse_address: textValue(values.delivery_warehouse_address),
          quote_cents: validated.quote_cents,
          quote_terms: textValue(values.quote_terms),
        });
        editingLocationId = '';
        await render();
      } catch (error) {
        if (formError) formError.textContent = error?.message || label('quoteAdmin.saveFailed', 'The delivery address and quote could not be saved.');
        if (submit) submit.disabled = false;
      }
    }, { signal });
    [...(host.querySelectorAll?.('[data-quote-admin-edit]') || [])].forEach((button) => {
      button.addEventListener('click', () => {
        const row = currentRows.find((item) => item.location_id === textValue(button.dataset?.locationId));
        const target = host.querySelector?.('[data-quote-admin-form]');
        if (!row || !target) return;
        editingLocationId = row.location_id;
        const set = (name, value) => {
          const field = target.querySelector(`[name="${name}"]`);
          if (field) field.value = value;
        };
        set('selection_label', row.selection_label);
        set('delivery_warehouse_address', row.delivery_warehouse_address);
        set('quote_dollars', row.quote_cents === null ? '' : (row.quote_cents / 100).toFixed(2));
        set('quote_terms', row.quote_terms);
        const title = target.querySelector('.quote-admin__form-title');
        if (title) title.textContent = label('quoteAdmin.editTitle', 'Edit Delivery Address & Quote');
        target.scrollIntoView?.({ block: 'nearest' });
        target.querySelector('[name="selection_label"]')?.focus?.();
      }, { signal });
    });
    // Reactivating is an upsert that carries the row's existing values back with
    // active: true - the callable rejects an upsert missing them, so this cannot
    // be a bare flag flip.
    [...(host.querySelectorAll?.('[data-quote-admin-activate]') || [])].forEach((button) => {
      button.addEventListener('click', async () => {
        const errorHost = host.querySelector?.('[data-quote-admin-error]');
        if (errorHost) errorHost.textContent = '';
        const row = currentRows.find((item) => item.location_id === textValue(button.dataset?.locationId));
        if (!row) return;
        button.disabled = true;
        try {
          await upsertLocation({
            customer_uid,
            location_id: row.location_id,
            selection_label: row.selection_label,
            delivery_warehouse_address: row.delivery_warehouse_address,
            quote_cents: row.quote_cents ?? 0,
            quote_terms: row.quote_terms,
            active: true,
          });
          await render();
        } catch (error) {
          if (errorHost) errorHost.textContent = error?.message || label('quoteAdmin.activateFailed', 'The delivery address could not be reactivated.');
          button.disabled = false;
        }
      }, { signal });
    });
    [...(host.querySelectorAll?.('[data-quote-admin-deactivate]') || [])].forEach((button) => {
      button.addEventListener('click', async () => {
        const errorHost = host.querySelector?.('[data-quote-admin-error]');
        if (errorHost) errorHost.textContent = '';
        button.disabled = true;
        try {
          await deactivateLocation({
            customer_uid,
            location_id: textValue(button.dataset?.locationId),
          });
          await render();
        } catch (error) {
          if (errorHost) errorHost.textContent = error?.message || label('quoteAdmin.deactivateFailed', 'The delivery address could not be deactivated.');
          button.disabled = false;
        }
      }, { signal });
    });
  };

  const render = async () => {
    const result = await listLocations({ customer_uid });
    if (destroyed) return;
    const locations = Array.isArray(result)
      ? result
      : Array.isArray(result?.locations) ? result.locations : [];
    if (!resolvedCustomerLabel) resolvedCustomerLabel = textValue(result?.customer_label);
    currentRows = quoteLocationRows(locations);
    host.innerHTML = quoteLocationAdminMarkup({
      customerLabel: resolvedCustomerLabel,
      rows: currentRows,
      role,
    }, { t });
    wire();
  };

  const ready = render().catch((error) => {
    if (destroyed) return;
    host.innerHTML = `<p class="quote-admin__load-error" role="alert">${escapeHtml(error?.message || label('quoteAdmin.loadFailed', 'Delivery addresses could not be loaded.'))}</p>`;
  });

  return Object.freeze({
    ready,
    destroy() {
      destroyed = true;
      controller.abort();
    },
  });
}
